import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import dangerIcon from "../../asserts/logo/Danger.svg";

const STATUS_LABEL = {
  not_reviewed: "Not reviewed",
  verified: "Verified",
  flagged: "Flagged",
  deactivated: "Deactivated",
};

const STATUS_SX = {
  not_reviewed: { width: 82, bgcolor: "#EDEDED", color: "#808080", fontWeight: 400 },
  verified: { bgcolor: "#DCFCE7", color: "#15803D", fontWeight: 600 },
  flagged: { width: 54, bgcolor: "#FFF2E5", color: "#ED7D00", fontWeight: 600 },
  deactivated: { bgcolor: "#F5F5F5", color: "#707070", fontWeight: 400 },
};

function itemDescription(tc) {
  return (tc?.name || "").trim();
}

/** Figma ModalPrompt — delete custom test case confirmation (single or bulk). */
export default function DeleteTestCaseDialog({
  testCase,
  testCases,
  onClose,
  onConfirm,
  submitting,
}) {
  const items =
    Array.isArray(testCases) && testCases.length > 0
      ? testCases
      : testCase
        ? [testCase]
        : [];
  const open = items.length > 0;
  const isBulk = items.length > 1;
  const count = items.length;
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          borderRadius: "4px",
          boxSizing: "border-box",
          width: { xs: "calc(100% - 32px)", sm: isBulk ? 600 : 480 },
          maxWidth: isBulk ? 600 : 480,
          height: isBulk ? { xs: "auto", sm: 426 } : "auto",
          maxHeight: { xs: "calc(100% - 32px)", sm: isBulk ? 426 : "none" },
          mx: 2,
          pt: "40px",
          pr: { xs: 3, sm: "64px" },
          pb: "40px",
          pl: { xs: 3, sm: "64px" },
          opacity: 1,
          transform: "rotate(0deg)",
          position: "relative",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          boxShadow:
            "0px 0px 10px 0px rgba(33, 34, 38, 0.12), 0px 10px 10px 0px rgba(0, 0, 0, 0.24)",
        },
      }}
      slotProps={{
        backdrop: {
          sx: { bgcolor: "rgba(52, 64, 84, 0.45)" },
        },
      }}
    >
      <IconButton
        size="small"
        onClick={onClose}
        aria-label="Close"
        disabled={submitting}
        sx={{
          position: "absolute",
          top: 8,
          right: 8,
          color: "#555555",
        }}
      >
        <CloseIcon sx={{ fontSize: 24 }} />
      </IconButton>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "16px",
          flex: 1,
          minHeight: 0,
          width: "100%",
        }}
      >
        <Box
          sx={{
            position: "relative",
            width: 64,
            height: 64,
            flexShrink: 0,
            opacity: 1,
          }}
        >
          <Box
            component="img"
            src={dangerIcon}
            alt=""
            sx={{
              position: "absolute",
              top: "12.8px",
              left: "10.67px",
              width: "43px",
              height: "39px",
              display: "block",
              transform: "rotate(0deg)",
              opacity: 1,
            }}
          />
        </Box>

        <Typography
          sx={{
            fontFamily: "Lato, sans-serif",
            fontWeight: 900,
            fontStyle: "normal",
            fontSize: "20px",
            lineHeight: "27px",
            letterSpacing: 0,
            color: "#000000",
            textAlign: "center",
            verticalAlign: "middle",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
          }}
        >
          {isBulk ? `Delete ${count} Test Cases?` : `Delete Test Case?`}
        </Typography>

        <Typography
          sx={{
            fontFamily: "Lato, sans-serif",
            fontWeight: 400,
            fontStyle: "normal",
            fontSize: "14px",
            lineHeight: "21px",
            letterSpacing: 0,
            color: "#555555",
            textAlign: "center",
            verticalAlign: "middle",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
          }}
        >
          {isBulk
            ? "This will permanently delete the following test cases and all associated validation data. This action cannot be undone."
            : "This action cannot be undone. The test case and all associated validation data will be permanently removed."}
        </Typography>

        {isBulk ? (
          <Box
            sx={{
              boxSizing: "border-box",
              width: 472,
              maxWidth: "100%",
              height: "auto",
              maxHeight: 93,
              opacity: 1,
              transform: "rotate(0deg)",
              borderRadius: "6px",
              pt: "12px",
              pr: "16px",
              pb: "12px",
              pl: "16px",
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              bgcolor: "#F5F5F5",
              border: "1px solid #E6E6E6",
              overflowY: "auto",
              flexShrink: 0,
            }}
          >
            {items.map((tc) => (
              <Box
                key={tc.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  minWidth: 0,
                  minHeight: 0,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "Lato, sans-serif",
                    fontWeight: 700,
                    fontStyle: "normal",
                    fontSize: "13px",
                    lineHeight: "100%",
                    letterSpacing: 0,
                    color: "#333333",
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                  }}
                >
                  {tc.name || "—"}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "Lato, sans-serif",
                    fontWeight: 400,
                    fontStyle: "normal",
                    fontSize: "13px",
                    lineHeight: "100%",
                    letterSpacing: 0,
                    color: "#808080",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  {itemDescription(tc)}
                </Typography>
                <Chip
                  label={STATUS_LABEL[tc.status] || tc.status}
                  size="small"
                  sx={{
                    boxSizing: "border-box",
                    width: "auto",
                    height: 17,
                    minHeight: 17,
                    borderRadius: "10px",
                    px: "8px",
                    py: "2px",
                    opacity: 1,
                    transform: "rotate(0deg)",
                    flexShrink: 0,
                    fontFamily: "Lato, sans-serif",
                    fontStyle: "normal",
                    fontSize: "11px",
                    lineHeight: "100%",
                    letterSpacing: 0,
                    ...(STATUS_SX[tc.status] || STATUS_SX.not_reviewed),
                    "& .MuiChip-label": {
                      px: 0,
                      py: 0,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    },
                  }}
                />
              </Box>
            ))}
          </Box>
        ) : null}

        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: "8px",
            pt: isBulk ? 0 : "16px",
            flexWrap: "wrap",
            flexShrink: 0,
          }}
        >
          <Button
            variant="outlined"
            onClick={onClose}
            disabled={submitting}
            sx={{
              textTransform: "none",
              fontFamily: "Lato, sans-serif",
              fontWeight: 700,
              fontSize: 15,
              lineHeight: "24px",
              color: "#555555",
              borderColor: "#BBBBBB",
              borderRadius: "4px",
              px: "16px",
              py: "8px",
              minHeight: 40,
              bgcolor: "#F5F5F5",
              backgroundImage:
                "linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.04) 100%)",
              boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.08)",
              "&:hover": {
                bgcolor: "#EEEEEE",
                borderColor: "#BBBBBB",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={onConfirm}
            disabled={submitting}
            sx={{
              textTransform: "none",
              fontFamily: "Lato, sans-serif",
              fontWeight: 700,
              fontSize: 15,
              lineHeight: "24px",
              color: "#FFFFFF",
              borderRadius: "4px",
              px: "16px",
              py: "8px",
              minHeight: 40,
              bgcolor: "#DE350B",
              backgroundImage:
                "linear-gradient(180deg, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0) 100%)",
              boxShadow: "0px 1px 2px 0px rgba(33, 34, 38, 0.2)",
              "&:hover": { bgcolor: "#BF2600", boxShadow: "0px 1px 2px 0px rgba(33, 34, 38, 0.2)" },
              "&.Mui-disabled": {
                bgcolor: "#DE350B",
                color: "#FFFFFF",
                opacity: 0.6,
              },
            }}
          >
            {submitting ? (
              <CircularProgress size={22} sx={{ color: "#fff" }} />
            ) : isBulk ? (
              `Delete ${count} Test Cases`
            ) : (
              "Delete Test case"
            )}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
}
